import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  Restaurant as RestaurantIcon,
  QrCode2 as QrCode2Icon,
  Payments as PaymentsIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Timer as TimerIcon,
  Receipt as ReceiptIcon,
  Security as SecurityIcon,
  QuestionAnswer as QuestionAnswerIcon,
 LocalDining as LocalDiningIcon
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import PageTitle from "../components/PageTitle";
import FAQBanner from "../assets/images/FAQ.avif";

const FAQ = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState(null);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // FAQ Categories
  const faqCategories = [
    { label: "All", count: 18 },
    { label: "Getting Started", count: 6 },
    { label: "Billing & Pricing", count: 4 },
    { label: "QR & Menu", count: 4 },
    { label: "Orders & Payments", count: 4 },
  ];

  // FAQ Data
  const faqs = [
    // Getting Started
    {
      id: "panel1",
      category: "Getting Started",
      question: "How do I register my restaurant on RestaurantWala?",
      answer:
        "Registering your restaurant is simple! Click on the 'Register Restaurant' button on our homepage. Fill in your restaurant details, owner name, contact information, and create a password. Once registered, you can start setting up your menu and generating QR codes within minutes.",
      icon: <RestaurantIcon sx={{ color: "var(--primary)" }} />,
    },
    {
      id: "panel2",
      category: "Getting Started",
      question: "Is there a free trial available?",
      answer:
        "Yes! We offer a 30-day free trial with full access to all features. No credit card required. After 30 days, you can continue using the service for just ₹299/month. You'll receive an invoice on day 30 with a 3-day grace period for payment.",
      icon: <TimerIcon sx={{ color: "var(--primary)" }} />,
    },
    {
      id: "panel3",
      category: "Getting Started",
      question: "Do I need any technical skills to use RestaurantWala?",
      answer:
        "No technical skills required! RestaurantWala is designed to be user-friendly for everyone. Our intuitive dashboard allows you to manage your menu, track orders, and generate QR codes with just a few clicks. We also provide 24/7 support to help you get started.",
      icon: <HelpIcon sx={{ color: "var(--primary)" }} />,
    },
    {
      id: "panel4",
      category: "Getting Started",
      question: "What type of businesses can use RestaurantWala?",
      answer:
        "RestaurantWala is perfect for all food businesses including Restaurants, Cafes, Fast Food Chains, Hotels, Cloud Kitchens, Food Courts, and even small food stalls. If you serve food to customers, RestaurantWala can help you digitize your operations.",
      icon: <RestaurantIcon sx={{ color: "var(--primary)" }} />,
    },
    {
      id: "panel5",
      category: "Getting Started",
      question: "Can I manage multiple restaurants from one account?",
      answer:
        "Yes! Our enterprise plan allows you to manage multiple restaurant locations from a single dashboard. Each restaurant gets its own QR codes, menu, and order management system while you can view consolidated reports and analytics.",
      icon: <PeopleIcon sx={{ color: "var(--primary)" }} />,
    },
    {
      id: "panel6",
      category: "Getting Started",
      question: "What support do you provide for new restaurants?",
      answer:
        "We provide comprehensive onboarding support including video tutorials, step-by-step guides, and 24/7 customer support. Our team is available via WhatsApp, email, and phone to help you with any questions or issues.",
      icon: <HelpIcon sx={{ color: "var(--primary)" }} />,
    },

    // Billing & Pricing
    {
      id: "panel7",
      category: "Billing & Pricing",
      question: "How does the billing process work?",
      answer:
        "Our billing process is simple and transparent. You get 30 days of free usage. On day 30, an invoice is automatically generated. You have 3 days grace period to make the payment of ₹299. After payment, your service continues uninterrupted. If payment is not made within the grace period, QR codes are temporarily deactivated.",
      icon: <ReceiptIcon sx={{ color: "var(--primary-dark)" }} />,
    },
    {
      id: "panel8",
      category: "Billing & Pricing",
      question: "What happens if I miss the payment deadline?",
      answer:
        "We provide a 3-day grace period after the invoice is generated. If payment is not received within this period, your QR codes will be deactivated to ensure fair usage. Don't worry - you can reactivate your service anytime by making the payment. Your data remains safe and secure.",
      icon: <SecurityIcon sx={{ color: "var(--primary-dark)" }} />,
    },
    {
      id: "panel9",
      category: "Billing & Pricing",
      question: "Are there any hidden charges?",
      answer:
        "Absolutely no hidden charges! The ₹299/month plan includes everything you need - unlimited QR menus, unlimited orders, menu management, staff management, WhatsApp notifications, and all other features. No setup fees, no transaction fees, no surprises.",
      icon: <CheckCircleIcon sx={{ color: "var(--primary-dark)" }} />,
    },
    {
      id: "panel10",
      category: "Billing & Pricing",
      question: "Can I cancel my subscription anytime?",
      answer:
        "Yes, you can cancel your subscription anytime with no questions asked. There are no long-term contracts or cancellation fees. If you cancel, you'll continue to have access until the end of your paid period.",
      icon: <CheckCircleIcon sx={{ color: "var(--primary-dark)" }} />,
    },

    // QR & Menu
    {
      id: "panel11",
      category: "QR & Menu",
      question: "How do QR codes work?",
      answer:
        "Each table in your restaurant gets a unique QR code. Customers simply scan the QR code using their phone camera. The digital menu opens instantly on their device - no app download required! They can view items, prices, images, check stock availability, and place orders directly.",
      icon: <QrCode2Icon sx={{ color: "var(--primary)" }} />,
    },
    {
      id: "panel12",
      category: "QR & Menu",
      question: "Can I customize my digital menu?",
      answer:
        "Absolutely! You have full control over your digital menu. Add categories (Starters, Main Course, Beverages, Desserts), items with descriptions, images, prices, and stock quantities. You can also update your menu anytime - changes reflect immediately.",
      icon: <QrCode2Icon sx={{ color: "var(--primary)" }} />,
    },
    {
      id: "panel13",
      category: "QR & Menu",
      question: "Can I upload menu items in bulk?",
      answer:
        "Yes! We provide an Excel template that you can download, fill with all your menu items, and upload in bulk. This makes it easy to set up your entire menu quickly, especially if you have many items.",
      icon: <QrCode2Icon sx={{ color: "var(--primary)" }} />,
    },
    {
      id: "panel14",
      category: "QR & Menu",
      question: "What happens when an item is out of stock?",
      answer:
        "You can mark items as 'Out of Stock' from your dashboard. The item will automatically be hidden or shown as unavailable on the customer's digital menu. This helps manage customer expectations and reduces order cancellations.",
      icon: <QrCode2Icon sx={{ color: "var(--primary)" }} />,
    },

    // Orders & Payments
    {
      id: "panel15",
      category: "Orders & Payments",
      question: "How do I receive orders from customers?",
      answer:
        "When a customer places an order, you receive it instantly in three ways: 1) Real-time notification on your restaurant dashboard, 2) WhatsApp message on your registered number, and 3) Display on the kitchen screen. You never miss an order!",
      icon: <ShoppingCartIcon sx={{ color: "var(--primary-dark)" }} />,
    },
    {
      id: "panel16",
      category: "Orders & Payments",
      question: "What payment methods can I accept?",
      answer:
        "RestaurantWala supports multiple payment methods to give your customers flexibility: Cash, UPI (Google Pay, PhonePe, Paytm), Credit/Debit Cards, and Counter Payment. All payments go directly to your restaurant account.",
      icon: <PaymentsIcon sx={{ color: "var(--primary-dark)" }} />,
    },
    {
      id: "panel17",
      category: "Orders & Payments",
      question: "Can customers order for delivery?",
      answer:
        "Yes! Customers can choose from three order types: Dine-in (for in-restaurant dining), Takeaway (order and pickup), and Delivery (deliver to their doorstep). You have full flexibility to offer all or any of these options.",
      icon: <ShoppingCartIcon sx={{ color: "var(--primary-dark)" }} />,
    },
    {
      id: "panel18",
      category: "Orders & Payments",
      question: "Do customers need to register to order?",
      answer:
        "No! One of the best features of RestaurantWala is that customers can order without any registration. They just scan the QR code and start ordering. This removes friction and increases order conversion.",
      icon: <PeopleIcon sx={{ color: "var(--primary-dark)" }} />,
    },
  ];

  // Filter FAQs based on search
  const filteredFaqs = faqs.filter((faq) => {
    const searchMatch =
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return searchMatch;
  });

  // Get unique categories for filter
  const categories = [...new Set(faqs.map((faq) => faq.category))];

  return (
    <>
      <PageTitle
        title="RestaurantWala FAQ – Frequently Asked Questions"
        description="Find answers to common questions about RestaurantWala, including pricing, QR menus, orders, billing, restaurant management and account setup."
        keywords="RestaurantWala FAQ, restaurant software FAQ, restaurant management FAQ, restaurant POS FAQ"
      />
      <Navbar />

      {/* Page Banner */}
      <PageBanner
        title="Frequently Asked Questions"
        subtitle="Find answers to the most common questions about RestaurantWala"
        chipLabel="FAQ"
        bgImage={FAQBanner}
      />

      <Box
        sx={{
          bgcolor: "#ffffff",
          minHeight: "100vh",
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Search Bar */}
          <Box
            sx={{
              maxWidth: "600px",
              mx: "auto",
              mb: { xs: 3, md: 4 },
            }}
          >
            <TextField
              fullWidth
              placeholder="Search for answers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: "var(--text-light)", mr: 1 }} />
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  bgcolor: "var(--bg-light)",
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&:hover fieldset": {
                    borderColor: "var(--primary)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "var(--primary)",
                  },
                },
              }}
            />
          </Box>

          {/* Category Filters */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 1, md: 1.5 },
              justifyContent: "center",
              mb: { xs: 3, md: 4 },
            }}
          >
            {faqCategories.map((category, index) => (
              <Chip
                key={index}
                label={`${category.label} (${category.count})`}
                sx={{
                  bgcolor:
                    index === 0 ? "var(--bg-primary-soft)" : "var(--bg-light)",
                  color: index === 0 ? "var(--primary)" : "var(--text-light)",
                  fontWeight: 600,
                  px: 1,
                  border:
                    index === 0
                      ? "2px solid var(--primary)"
                      : "1px solid var(--border)",
                  "&:hover": {
                    bgcolor: "var(--bg-primary-soft)",
                  },
                }}
              />
            ))}
          </Box>

          {/* FAQ Accordion */}
          <Grid container spacing={4}>
            <Grid item size={{ xs: 12, md: 8 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: "20px",
                  bgcolor: "var(--bg-light)",
                  border: "1px solid var(--border)",
                }}
              >
                {filteredFaqs.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 5 }}>
                    <Typography sx={{ fontSize: "3rem" }}>🔍</Typography>
                    <Typography sx={{ color: "var(--text-light)", mt: 2 }}>
                      No results found for "{searchTerm}"
                    </Typography>
                    <Button
                      variant="outlined"
                      sx={{
                        mt: 2,
                        borderColor: "var(--primary)",
                        color: "var(--primary)",
                      }}
                      onClick={() => setSearchTerm("")}
                    >
                      Clear Search
                    </Button>
                  </Box>
                ) : (
                  filteredFaqs.map((faq) => (
                    <Accordion
                      key={faq.id}
                      expanded={expanded === faq.id}
                      onChange={handleChange(faq.id)}
                      sx={{
                        bgcolor: "#ffffff",
                        borderRadius: "12px !important",
                        mb: 1.5,
                        border: "1px solid var(--border)",
                        boxShadow: "none",
                        "&:before": {
                          display: "none",
                        },
                        "&.Mui-expanded": {
                          borderColor: "var(--primary)",
                          boxShadow: "var(--shadow-orange)",
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon sx={{ color: "var(--primary)" }} />
                        }
                        sx={{
                          "& .MuiAccordionSummary-content": {
                            alignItems: "center",
                            gap: 2,
                          },
                          "&:hover": {
                            bgcolor: "var(--bg-primary-soft)",
                          },
                          borderRadius: "12px",
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "var(--bg-primary-soft)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "var(--primary)",
                            flexShrink: 0,
                          }}
                        >
                          {faq.icon}
                        </Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "14px", md: "15px" },
                            color: "var(--text)",
                          }}
                        >
                          {faq.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ pl: { xs: 0, md: 6 } }}>
                          <Divider sx={{ mb: 2 }} />
                          <Typography
                            sx={{
                              color: "var(--text-light)",
                              fontSize: { xs: "14px", md: "15px" },
                              lineHeight: 1.8,
                            }}
                          >
                            {faq.answer}
                          </Typography>
                          <Chip
                            label={faq.category}
                            size="small"
                            sx={{
                              mt: 2,
                              bgcolor: "var(--bg-primary-soft)",
                              color: "var(--primary)",
                              fontSize: "11px",
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))
                )}
              </Paper>
            </Grid>

            {/* Sidebar */}
            <Grid item size={{ xs: 12, md: 4 }}>
              <Stack spacing={3}>
                {/* Quick Links Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: "20px",
                    bgcolor: "#ffffff",
                    border: "1px solid var(--border)",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      color: "var(--text)",
                      mb: 2,
                    }}
                  >
                    Quick Links 
                  </Typography>
                  <Stack spacing={1.5}>
                    {[
                      {
                        label: "Register Restaurant",
                        icon: <RestaurantIcon />,
                        path: "/register",
                      },
                      {
                        label: "Pricing Plans",
                        icon: <PaymentsIcon />,
                        path: "/pricing",
                      },
                       {
                        label: "Restaurants",
                        icon: <LocalDiningIcon />,
                        path: "/restaurants",
                      },
                      {
                        label: "Contact Support",
                        icon: <HelpIcon />,
                        path: "/contact",
                      },
                      {
                        label: "Features",
                        icon: <TimerIcon />,
                        path: "/features",
                      },
                    ].map((link, index) => (
                      <Button
                        key={index}
                        component={Link}
                        to={link.path}
                        startIcon={link.icon}
                        fullWidth
                        sx={{
                          justifyContent: "flex-start",
                          color: "var(--text-light)",
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: "10px",
                          p: 1.5,
                          "&:hover": {
                            bgcolor: "var(--bg-primary-soft)",
                            color: "var(--primary)",
                          },
                        }}
                      >
                        {link.label}
                      </Button>
                    ))}
                  </Stack>
                </Paper>

                {/* Support Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: "20px",
                    background:
                      "linear-gradient(135deg, var(--primary-dark), var(--primary))",
                    color: "#fff",
                    textAlign: "center",
                    boxShadow: "var(--shadow-orange-hover)",
                  }}
                >
                  <Typography sx={{ fontSize: "2.5rem", mb: 1 }}>💬</Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.2rem",
                      mb: 1,
                    }}
                  >
                    Still Have Questions?
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      opacity: 0.9,
                      mb: 2,
                    }}
                  >
                    Our support team is here to help you
                  </Typography>
                  <Stack spacing={1.5}>
                    <Button
                      variant="contained"
                      startIcon={<WhatsAppIcon />}
                      fullWidth
                      sx={{
                        bgcolor: "#fff",
                        color: "var(--primary)",
                        fontWeight: 700,
                        borderRadius: "10px",
                        "&:hover": {
                          bgcolor: "#f0f0f0",
                          transform: "scale(1.05)",
                        },
                      }}
                      href="https://wa.me/916378788756?text=Hello RestaurantWala%20Team%2C%20I%20would%20like%20to%20inquire%20about%20your%20services."
                      target="_blank"
                    >
                      WhatsApp Us
                    </Button>
                    {/* <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      fullWidth
                      sx={{
                        color: "#fff",
                        borderColor: "rgba(255,255,255,0.5)",
                        borderRadius: "10px",
                        "&:hover": {
                          borderColor: "#fff",
                          bgcolor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      restaurantwala24@gmail.com
                    </Button> */}
                    <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      fullWidth
                      component="a"
                      href="mailto:restaurantwala24@gmail.com"
                      sx={{
                        color: "#fff",
                        borderColor: "rgba(255,255,255,0.5)",
                        borderRadius: "10px",
                        textTransform: "none",
                        "&:hover": {
                          borderColor: "#fff",
                          bgcolor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      restaurantwala24@gmail.com
                    </Button>
                  </Stack>
                </Paper>

                {/* Contact Info */}
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: "20px",
                    bgcolor: "#ffffff",
                    border: "1px solid var(--border)",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      color: "var(--text)",
                      mb: 2,
                    }}
                  >
                    Contact Information
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <PhoneIcon
                        sx={{ color: "var(--primary)", fontSize: 20 }}
                      />
                      <Typography
                        sx={{ color: "var(--text-light)", fontSize: "14px" }}
                      >
                        +91 6378788756
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <EmailIcon
                        sx={{ color: "var(--primary)", fontSize: 20 }}
                      />
                      <Typography
                        sx={{ color: "var(--text-light)", fontSize: "14px" }}
                      >
                        restaurantwala24@gmail.com
                      </Typography>
                    </Box>
                    {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <WhatsAppIcon sx={{ color: "var(--primary)", fontSize: 20 }} />
                      <Typography sx={{ color: "var(--text-light)", fontSize: "14px" }}>
                        +91 6378788756
                      </Typography>
                    </Box> */}
                  </Stack>
                </Paper>

                {/* Trust Badge */}
                {/* <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: "20px",
                    bgcolor: "#ffffff",
                    border: "1px solid var(--border)",
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 0.5,
                      mb: 1,
                    }}
                  >
                    {[...Array(5)].map((_, i) => (
                      <StarIcon
                        key={i}
                        sx={{ color: "#FFD700", fontSize: 20 }}
                      />
                    ))}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "var(--text)",
                      fontSize: "0.95rem",
                    }}
                  >
                    4.8/5 from 500+ Restaurants
                  </Typography>
                  <Typography
                    sx={{ color: "var(--text-light)", fontSize: "13px" }}
                  >
                    Trusted by restaurant owners nationwide
                  </Typography>
                </Paper> */}
              </Stack>
            </Grid>
          </Grid>

          {/* Still Have Questions? CTA */}
          <Paper
            elevation={0}
            sx={{
              mt: { xs: 4, md: 6 },
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: "24px",
              bgcolor: "var(--bg-light)",
              border: "1px solid var(--border)",
              textAlign: "center",
            }}
          >
            {/* <Typography sx={{ fontSize: "2.5rem", mb: 1 }}>🚀</Typography> */}
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.3rem", sm: "1.5rem", md: "1.8rem" },
                color: "var(--text)",
                mb: 1,
              }}
            >
              Still Have Questions?
            </Typography>
            <Typography
              sx={{
                color: "var(--text-light)",
                fontSize: { xs: "14px", md: "16px" },
                mb: 3,
              }}
            >
              We're here to help you get started with RestaurantWala
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                component={Link}
                to="/contact"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: "var(--primary)",
                  color: "#fff",
                  px: 4,
                  py: 1.5,
                  borderRadius: "14px",
                  fontWeight: 700,
                  boxShadow: "var(--shadow-orange)",
                  "&:hover": {
                    bgcolor: "var(--primary-dark)",
                    transform: "scale(1.05)",
                    boxShadow: "var(--shadow-orange-hover)",
                  },
                }}
              >
                Contact Us
              </Button>
              <Button
                variant="outlined"
                component={Link}
                to="/register"
                sx={{
                  color: "var(--text)",
                  borderColor: "var(--border)",
                  px: 4,
                  py: 1.5,
                  borderRadius: "14px",
                  fontWeight: 700,
                  "&:hover": {
                    borderColor: "var(--primary)",
                    bgcolor: "var(--bg-primary-soft)",
                    color: "var(--primary)",
                  },
                }}
              >
                Register Now
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default FAQ;
